/* eslint-disable operator-linebreak */
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@hqo/ui-atoms';
import { Pin } from 'lucide-react';
import { useIntl } from 'react-intl';

import { setFiltersPinned } from 'store/visits/actions';
import { getFiltersPinned, getAllFilters } from 'store/visits/selectors';
import { saveFiltersToStorage, clearFiltersFromStorage } from 'store/visits/util';
import { cn } from '@hqo/react-hooks';

const pinnedClassName =
  'text-system-blue border-foreground/10 bg-secondary hover:bg-secondary/80 hover:text-system-blue-hover';

export const FiltersToolbarPinButton = (): JSX.Element => {
  const dispatch = useDispatch();
  const isPinned = useSelector(getFiltersPinned);
  const filters = useSelector(getAllFilters);
  const { formatMessage } = useIntl();
  const pinLabel = formatMessage({ id: 'labels.filtersToolbar.pinFilters', defaultMessage: 'Pin filters' });
  const unpinLabel = formatMessage({ id: 'labels.filtersToolbar.unpinFilters', defaultMessage: 'Unpin filters' });
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handlePinToggle = useCallback((): void => {
    if (isPinned) {
      // Unpinning: clear localStorage and update state
      clearFiltersFromStorage();
      dispatch(setFiltersPinned(false));
    } else {
      // Pinning: save current filters to localStorage and update state
      saveFiltersToStorage(filters);
      dispatch(setFiltersPinned(true));
    }
  }, [isPinned, filters, dispatch]);

  const handleMouseEnter = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handleTouchStart = useCallback(() => {
    setTooltipOpen(!tooltipOpen);
  }, [tooltipOpen]);

  const handleFocus = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={tooltipOpen}>
        <TooltipTrigger
          className="w-8 h-8"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          <Button
            variant="outline"
            className={cn(
              'justify-center w-8 h-8 has-[>svg]:p-0.5 rounded-lg text-muted-foreground ml-auto',
              isPinned && pinnedClassName,
            )}
            data-testid="pin-filters"
            data-ispinned={isPinned}
            onClick={handlePinToggle}
          >
            <Pin className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isPinned ? unpinLabel : pinLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
